import { isDefined } from '@e-socna/utils';
import {
  calculateMinDelayForRate,
  handleNetworkError,
  handleServerError,
} from '@e-socna/gis';
import type {
  RetryContext,
  RetryDecision,
  RetryStrategyFn,
} from '@e-socna/better-fetch';

import { MAX_RETRY_DELAY_MS } from '../constants/delay.constant';
import { RECHERCHE_ENTREPRISES_RATE_LIMITS } from '../constants/rate-limits.constant';

/**
 * Create a retry strategy for the "Recherche d'entreprises" API
 *
 * @param serviceType - Type of service (only "search" for now)
 * @returns Retry strategy function configured for the service rate limit
 *
 * @example
 * ```typescript
 * import { rechercheEntreprisesRetryStrategy } from '@e-socna/organization-search';
 *
 * const data = await betterFetch(url, {}, {
 *   retryStrategy: rechercheEntreprisesRetryStrategy('search'),
 * });
 * ```
 */
export const rechercheEntreprisesRetryStrategy =
  (
    serviceType: keyof typeof RECHERCHE_ENTREPRISES_RATE_LIMITS = 'search',
  ): RetryStrategyFn =>
  ({ statusCode, attempt, retryAfterMs }: RetryContext): RetryDecision => {
    // Network errors and timeouts
    if (typeof statusCode === 'undefined') {
      return handleNetworkError(attempt);
    }

    // 429 Rate Limit
    if (statusCode === 429) {
      if (isDefined(retryAfterMs)) {
        return {
          shouldRetry: true,
          delayMs: Math.min(retryAfterMs, MAX_RETRY_DELAY_MS),
        };
      }

      // Calculate delay based on service rate limit
      const rateLimit = RECHERCHE_ENTREPRISES_RATE_LIMITS[serviceType];
      const minDelay = calculateMinDelayForRate(rateLimit);
      const backoffDelay = minDelay * 2 ** attempt;

      return {
        shouldRetry: true,
        delayMs: Math.min(backoffDelay, MAX_RETRY_DELAY_MS),
      };
    }

    // 5xx Server errors
    if (statusCode >= 500 && statusCode < 600) {
      return handleServerError(attempt);
    }

    return { shouldRetry: false, delayMs: 0 };
  };
